import { lazy, memo, Suspense, useEffect } from 'react'
import { Switch, Route, Router, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { forceHideModal } from '../actions/_modal'
import LoadingSpinner from '../components/mini/LoadingSpinner'
import { requireAuthentication } from '../containers/HOC/RequireAuthentication'
import { restrictLearnerAccess } from '../containers/HOC/RestrictLearnerAccess'
import { restrictMentorAccess } from '../containers/HOC/RestrictMentorAccess'

const NotFound = lazy(() => import('../containers/NotFound'))
const CreateGuardianUser = lazy(() => import('../containers/CreateGuardian/CreateGuardianUser'))
const GuardianLobby = lazy(() =>
  import('../components/GuardianLobby/GuardianLobby'))
const Error = lazy(() => import('../containers/Error'))
const Login = lazy(() => import('../containers/Account/Login'))
const MyArtifacts = lazy(() => import('../containers/MyArtifacts/MyArtifacts'))
const MyCommunity = lazy(() => import('../containers/MyCommunity/MyCommunity'))
const MentorDashboardContainer = lazy(() => import('../containers/Mentor/MentorDashboard'))
const MyStudents = lazy(() => import('../containers/MyStudents/MyStudents'))
const RouteCourses = lazy(() => import('../containers/MyCourses/MyCourses'))
const MyStudentsSkillsMap = lazy(() => import('../containers/MyStudents/SkillsMap'))
const Engagement = lazy(() => import('../containers/Engagement/Engagement'))
const OurPolicies = lazy(() => import('../containers/Policies/OurPolicies'))
const Policies = lazy(() => import('../containers/Policies/Policies'))
const ProfileContainer = lazy(() => import('../containers/Profile/Profile'))
const ResetPassword = lazy(() => import('../containers/Account/ResetPassword'))
const UnlockAccount = lazy(() => import('../containers/Account/UnlockAccount'))
const StudentProfileContainer = lazy(() => import('../containers/StudentProfile/StudentProfile'))
const TermlyReports = lazy(() => import('../containers/Mentor/TermlyReports'))
const UserCompiledQuest = lazy(() => import('../containers/Student/UserCompiledQuest'))
const StudentDashboard = lazy(() => import('../containers/StudentDashboard/StudentDashboard'))
const Trends = lazy(() => import('../containers/Trends/index'))

const UserCompiledQuestAuthenticated = requireAuthentication(UserCompiledQuest)
const MentorDashboardContainerAuthenticated = requireAuthentication(
  restrictLearnerAccess(MentorDashboardContainer, true)
)
const MyArtifactsAuthenticated = requireAuthentication(
  restrictMentorAccess(MyArtifacts)
)
const MyCommunityAuthenticated = requireAuthentication(MyCommunity)
const MyStudentsAuthenticated = requireAuthentication(
  restrictLearnerAccess(MyStudents, true)
)
const TrendsAuthenticated = requireAuthentication(
  restrictLearnerAccess(Trends, true)
)
const MyStudentsSkillsMapAuthenticated = requireAuthentication(
  restrictLearnerAccess(MyStudentsSkillsMap, true)
)
const OurPoliciesAuthenticated = requireAuthentication(OurPolicies)
const ProfileContainerAuthenticated = requireAuthentication(ProfileContainer)
const TermlyReportsAuthenticated = requireAuthentication(
  restrictLearnerAccess(TermlyReports, true)
)
const StudentProfileContainerAuthenticated = requireAuthentication(
  restrictLearnerAccess(StudentProfileContainer, true)
)
const RouteCoursesAuthenticated = requireAuthentication(
  restrictLearnerAccess(RouteCourses)
)
const RouteEngagementAuthenticated = requireAuthentication(
  restrictMentorAccess(Engagement)
)
const StudentDashboardAuthenticated = requireAuthentication(
  restrictMentorAccess(StudentDashboard)
)

const Routes = ({ history }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const isModalOpen = useSelector(state => state._modal.modalType !== null)

  useEffect(() => {
    if (isModalOpen) {
      dispatch(forceHideModal())
    }
  }, [location])

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Router history={history}>
        <Switch>
          {/* Mentor viewing student UCQ/work routes */}
          <Route
            exact
            path="/student/:studentid/ucq/:usercompiledquestid"
            component={UserCompiledQuestAuthenticated}
          />
          <Route
            exact
            path="/student/:studentid/ucq/:usercompiledquestid/activities/:activityid"
            component={UserCompiledQuestAuthenticated}
          />
          {/* Student viewing their own UCQ/work routes */}
          <Route
            exact
            path="/ucq/:usercompiledquestid"
            component={UserCompiledQuestAuthenticated}
          />
          <Route
            exact
            path="/ucq/:usercompiledquestid/activities/:activityid"
            component={UserCompiledQuestAuthenticated}
          />
          {/* Mentor or student viewing CQ routes */}
          <Route
            exact
            path="/cs/:coursesectionid/level/:levelid/cq/:compiledquestid"
            component={UserCompiledQuestAuthenticated}
          />
          <Route
            exact
            path="/cs/:coursesectionid/level/:levelid/cq/:compiledquestid/activities/:activityid"
            component={UserCompiledQuestAuthenticated}
          />
          <Route exact path="/login/:view" component={Login} />
          <Route exact path="/login" component={Login} />
          <Route exact path="/" component={Login} />
          <Route
            path="/mentordashboard"
            component={MentorDashboardContainerAuthenticated}
          />
          <Route
            exact
            path="/myartifacts"
            component={MyArtifactsAuthenticated}
          />
          {/*Student Dashboard Routes*/}
          <Route
            exact
            path="/user/:userid/dashboard"
            component={StudentDashboardAuthenticated}
          />
          <Route
            exact
            path="/user/:userid/dashboard/reports"
            component={StudentDashboardAuthenticated}
          />
          <Route
            exact
            path="/user/:userid/dashboard/schedule"
            component={StudentDashboardAuthenticated}
          />
          <Route
            exact
            path="/engagement"
            component={RouteEngagementAuthenticated}
          />
          <Route
            exact
            path="/mycommunity"
            component={MyCommunityAuthenticated}
          />
          <Route
            exact
            path="/mystudents/journeyoverview"
            component={MyStudentsAuthenticated}
          />
          <Route
            exact
            path="/mystudents/announcements"
            component={MyStudentsAuthenticated}
          />
          <Route
            exact
            path="/mystudents/engagement"
            component={MyStudentsAuthenticated}
          />
          <Route
            exact
            path="/mystudents/learningskills"
            component={MyStudentsSkillsMapAuthenticated}
          />
          <Route
            exact
            path="/mystudents/schedule"
            component={MyStudentsAuthenticated}
          />
          <Route
            exact
            path="/mystudents/trends"
            component={MyStudentsAuthenticated}
          />
          <Route exact path="/mystudents" component={MyStudentsAuthenticated} />
          <Route
            exact
            path="/ourpolicies"
            component={OurPoliciesAuthenticated}
          />
          <Route exact path="/policies" component={Policies} />
          <Route
            exact
            path="/profile/:id/:tab"
            component={ProfileContainerAuthenticated}
          />
          <Route
            exact
            path="/profile/:id"
            component={ProfileContainerAuthenticated}
          />
          <Route exact path="/resetpassword/:token" component={ResetPassword} />
          <Route exact path="/unlockaccount/:token" component={UnlockAccount} />
          <Route
            exact
            path="/term/:termid/coursesection/:coursesectionid/reports"
            component={TermlyReportsAuthenticated}
          />
          <Route exact path="/guardianlobby" component={GuardianLobby} />
          {/* Mentor student profile routes */}
          <Route
            exact
            path="/user/:userid"
            component={StudentProfileContainerAuthenticated}
          />
          <Route
            exact
            path="/user/:userid/performance"
            component={StudentProfileContainerAuthenticated}
          />
          <Route
            exact
            path="/user/:userid/studentcourses"
            component={StudentProfileContainerAuthenticated}
          />
          <Route
            exact
            path="/user/:userid/reports"
            component={StudentProfileContainerAuthenticated}
          />
          <Route
            exact
            path="/user/:userid/engagement"
            component={StudentProfileContainerAuthenticated}
          />
          <Route
            path="/user/:userid/feedback"
            component={StudentProfileContainerAuthenticated}
          />
          <Route
            exact
            path="/user/:userid/attendance"
            component={StudentProfileContainerAuthenticated}
          />
          <Route
            exact
            path="/user/:userid/schedule"
            component={StudentProfileContainerAuthenticated}
          />

          <Route
            exact
            path="/user/:userid/courses"
            component={RouteCoursesAuthenticated}
          />
          <Route
            exact
            path="/user/:userid/courses/page/:page"
            component={RouteCoursesAuthenticated}
          />
          <Route
            exact
            path="/user/:userid/courses/:courseid"
            component={RouteCoursesAuthenticated}
          />
          <Route
            exact
            path="/user/:userid/courses/feedback"
            component={RouteCoursesAuthenticated}
          />
          <Route
            exact
            path="/user/:userid/courses/notebook"
            component={RouteCoursesAuthenticated}
          />

          <Route path="/create_guardian" component={CreateGuardianUser} />
          <Route path="/loading" component={LoadingSpinner} />
          <Route path="/error" component={Error} />
          <Route path="*" component={NotFound} />
        </Switch>
      </Router>
    </Suspense>
  )
}

// specifically avoid re-renders when modal is shown/hidden in App.js
export default memo(Routes)
